import React, { useEffect, useState } from "react";
import { apiJson } from "../../../utils/api";

const SECTION_LABELS = {
  personalInfo: "Personal Information",
  permanentAddress: "Permanent Address",
  communicationAddress: "Communication Address",
};

const FormFieldConfig = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = () => {
    setLoading(true);
    apiJson("/api/admin/master/form-field-config")
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateRow = async (id, patch) => {
    const target = items.find((i) => i.id === id);
    if (!target) return;
    const optimistic = { ...target, ...patch };
    // Enforce client-side: hidden ⇒ optional
    if (!optimistic.isVisible) optimistic.isRequired = false;
    setItems(items.map((i) => (i.id === id ? optimistic : i)));
    setSavingId(id);
    setError("");
    setMessage("");
    try {
      await apiJson(`/api/admin/master/form-field-config/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          label: optimistic.label,
          isVisible: optimistic.isVisible,
          isRequired: optimistic.isRequired,
        }),
      });
      setMessage("Saved");
      setTimeout(() => setMessage(""), 1500);
    } catch (e) {
      setError(e.message);
      load();
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <p className="text-gray-500">Loading...</p>;

  // Group by section
  const sections = {};
  for (const i of items) {
    if (!sections[i.sectionKey]) sections[i.sectionKey] = [];
    sections[i.sectionKey].push(i);
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-2">Admission Form Field Configuration</h1>
      <p className="text-sm text-gray-600 mb-4">
        Toggle visibility, mark fields required, or override labels.
        System-required fields (marked with 🔒) cannot be hidden or made optional.
      </p>
      {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-3 text-sm">{error}</div>}
      {message && <div className="bg-green-50 text-green-700 p-3 rounded mb-3 text-sm">{message}</div>}

      {Object.keys(sections).map((key) => (
        <div key={key} className="bg-white rounded shadow mb-4">
          <h2 className="px-4 py-2 font-semibold text-gray-700 border-b bg-gray-50">
            {SECTION_LABELS[key] || key}
          </h2>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Field Key</th>
                <th className="text-left px-4 py-2">Label</th>
                <th className="text-center px-4 py-2">Visible</th>
                <th className="text-center px-4 py-2">Required</th>
              </tr>
            </thead>
            <tbody>
              {sections[key].map((it) => (
                <tr key={it.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">
                    {it.fieldKey} {it.isDbRequired && <span title="System required">🔒</span>}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={it.label}
                      onChange={(e) => setItems(items.map((i) => (i.id === it.id ? { ...i, label: e.target.value } : i)))}
                      onBlur={() => updateRow(it.id, { label: it.label })}
                      className="w-full px-2 py-1 border rounded text-sm"
                      disabled={savingId === it.id}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={it.isVisible}
                      disabled={it.isDbRequired || savingId === it.id}
                      onChange={(e) => updateRow(it.id, { isVisible: e.target.checked })}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={it.isRequired}
                      disabled={it.isDbRequired || !it.isVisible || savingId === it.id}
                      onChange={(e) => updateRow(it.id, { isRequired: e.target.checked })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default FormFieldConfig;
